import { useEffect, useState } from 'react';
import type { KBArticle } from '../data/kb-articles/types';
import { API_BASE_URL } from '../config/api';

interface UseKBArticlesResult {
  articles: KBArticle[];
  loading: boolean;
  error: Error | null;
}

/**
 * Hook to fetch KB articles for a specific frame.
 * Admin configuration is the single source of truth:
 * - If API returns no articles -> render none.
 * - No fallback to local/static KB mappings.
 */
export const useKBArticles = (
  frameId?: string | null
): UseKBArticlesResult => {
  const [articles, setArticles] = useState<KBArticle[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!frameId) {
      setArticles([]);
      setLoading(false);
      setError(null);
      return;
    }

    const fetchArticles = async () => {
      setLoading(true);
      setError(null);

      try {
        const frameNumber = frameId.replace('frame-', '');

        const response = await fetch(
          `${API_BASE_URL}/api/kb-articles/frame/${frameNumber}`
        );

        if (!response.ok) {
          throw new Error(`Failed to fetch KB articles (HTTP ${response.status})`);
        }

        const data = await response.json();

        const apiArticles: KBArticle[] =
          data?.success && Array.isArray(data?.data)
            ? data.data.map((article: unknown) => ({
                id: article.id,
                title: article.title,
                slug: article.slug,
                content: {
                  external: article.content,
                  internal: '',
                },
                tags: article.tags || [],
                relatedGuides: [],
                prompts: [],
              }))
            : [];

        setArticles(apiArticles);
      } catch (err) {
        setArticles([]);
        setError(err instanceof Error ? err : new Error('Failed to load KB articles'));
      } finally {
        setLoading(false);
      }
    };

    fetchArticles();
  }, [frameId]);

  return { articles, loading, error };
};